import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { EDIT_PAGE_KEY_MAP, useAutoSaveParams } from '@/pages/strategic/hooks/useAutoSaveParams';

/**
 * Custom hook for GR Management auto-save functionality
 * 
 * Manages auto-save detection and restoration for GR Management section with:
 * - Tracks selectedMilestone during edit mode
 * - Project and milestone-specific auto-save
 * 
 */
export const useGRManagementAutoSave = (
    isEditing: boolean,
    selectedMilestone: any,
    projectId: string,
    setSelectedMilestone: (data: any) => void
) => {
    // Auto-save state
    const [autoSaveVisible, setAutoSaveVisible] = useState(false);

    // Auto-save: construct unique pageKey by appending project ID and milestone ID
    // Each project+milestone combination has separate auto-save storage
    const grManagementPageKey = useMemo(() => {
        if (!projectId || !selectedMilestone?.milestone_id) return '';
        return `${EDIT_PAGE_KEY_MAP.studio_indev_gr_management}_${projectId}_${selectedMilestone.milestone_id}`;
    }, [projectId, selectedMilestone?.milestone_id]);

    // Auto-save: get methods (check if auto-save exists)
    const autoSaveMethods: any = useAutoSaveParams({
        pageKey: grManagementPageKey,
    });

    const {
        getSaveParamsValue,
        showRestorePrompt,
        deleteSaveParamsByPageKey
    } = autoSaveMethods;

    // Helper function to normalize attachment values (treat empty string and "[]" as equivalent)
    // This ensures that empty attachments are normalized for consistent comparison
    const normalizeAttachmentValue = useCallback((value: string | undefined | null): string => {
        if (!value || typeof value !== 'string' || value.trim() === '') return '';
        // If it's an empty array string, normalize to empty string
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed) && parsed.length === 0) {
                return '';
            }
            // If it's a valid non-empty array, return it as a normalized JSON string
            return JSON.stringify(parsed);
        } catch (e) {
            // If it's not valid JSON, return as-is
            return value;
        }
    }, []);

    // Current save params for auto-save comparison
    const currentSaveParams = useMemo(() => {
        if (!isEditing || !selectedMilestone) {
            // When not editing, return empty params to prevent auto-save from triggering
            return {
                grManagementData: {},
                projectId: '',
                milestoneId: '',
            };
        }

        // When editing, use selectedMilestone data directly with normalized attachment values
        return {
            grManagementData: {
                project_id: Number(projectId) || '',
                milestone_id: selectedMilestone?.milestone_id,
                gate_review_decision: selectedMilestone?.gate_review_decision || '',
                gr_attachments: normalizeAttachmentValue(selectedMilestone?.gr_attachments),
                key_feedback: selectedMilestone?.key_feedback || '',
                kf_attachments: normalizeAttachmentValue(selectedMilestone?.kf_attachments),
            },
            projectId: projectId,
            milestoneId: selectedMilestone.milestone_id,
        };
    }, [isEditing, selectedMilestone, projectId, normalizeAttachmentValue]);

    // Memoize initialParams to capture the starting state when entering edit mode
    const initialParamsRef = useRef<{
        grManagementData: any;
        projectId: string;
        milestoneId: string;
    }>({
        grManagementData: {},
        projectId: '',
        milestoneId: '',
    });

    // Update initialParams when entering edit mode (capture the original state)
    useEffect(() => {
        if (isEditing && selectedMilestone) {
            // Only update if initialParams is empty (first time entering edit mode)
            if (Object.keys(initialParamsRef.current.grManagementData).length === 0) {
                // Capture the initial GR Management data structure with normalized attachment values
                const initialData = {
                    project_id: Number(projectId) || '',
                    milestone_id: selectedMilestone?.milestone_id,
                    gate_review_decision: selectedMilestone?.gate_review_decision || '',
                    gr_attachments: normalizeAttachmentValue(selectedMilestone?.gr_attachments),
                    key_feedback: selectedMilestone?.key_feedback || '',
                    kf_attachments: normalizeAttachmentValue(selectedMilestone?.kf_attachments),
                };
                
                initialParamsRef.current = {
                    grManagementData: JSON.parse(JSON.stringify(initialData)), // Deep clone to avoid reference issues
                    projectId: projectId,
                    milestoneId: selectedMilestone.milestone_id,
                };
                console.log('🎯 [GR MANAGEMENT AUTO-SAVE] Captured initial data:', initialParamsRef.current);
            }
        } else if (!isEditing) {
            // Reset initialParams when exiting edit mode
            initialParamsRef.current = {
                grManagementData: {},
                projectId: '',
                milestoneId: '',
            };
        }
    }, [isEditing, selectedMilestone, projectId, normalizeAttachmentValue]);

    useAutoSaveParams({
        pageKey: grManagementPageKey,
        initialParams: initialParamsRef.current,
        saveParamsCur: currentSaveParams,
        isBegin: isEditing,
        isChangeValue: true,
    });

    // Handler: Check for auto-save before entering edit mode
    const handleGRManagementEdit = useCallback((originalEditHandler: () => void) => {
        if (!grManagementPageKey) {
            originalEditHandler();
            return;
        }

        if (showRestorePrompt(grManagementPageKey)) {
            setAutoSaveVisible(true);
        } else {
            originalEditHandler();
        }
    }, [showRestorePrompt, grManagementPageKey]);

    // Handler: Discard auto-save and enter edit mode
    const handleAutoSaveCancel = useCallback((originalEditHandler: () => void) => {
        setAutoSaveVisible(false);
        if (grManagementPageKey) {
            deleteSaveParamsByPageKey(grManagementPageKey);
        }
        originalEditHandler();
    }, [deleteSaveParamsByPageKey, grManagementPageKey]);

    // Handler: Restore from auto-save
    const handleAutoSaveConfirm = useCallback((originalEditHandler: () => void) => {
        const saveValue = getSaveParamsValue(grManagementPageKey) || {};

        console.log('🔄 [GR MANAGEMENT AUTO-SAVE RESTORE] Restoring from auto-save:', saveValue);

        setAutoSaveVisible(false);

        // Check if projectId or milestoneId changed (user switched projects/milestones)
        if (saveValue.projectId && saveValue.projectId !== projectId) {
            // Clear auto-save data for the wrong project
            if (grManagementPageKey) {
                deleteSaveParamsByPageKey(grManagementPageKey);
            }
            originalEditHandler();
            return;
        }

        if (saveValue.milestoneId && saveValue.milestoneId !== selectedMilestone?.milestone_id) {
            // Clear auto-save data for the wrong milestone
            if (grManagementPageKey) {
                deleteSaveParamsByPageKey(grManagementPageKey);
            }
            originalEditHandler();
            return;
        }

        // Update selectedMilestone directly with data from auto-save
        // Normalize attachment values when restoring to ensure consistency
        if (saveValue.grManagementData && Object.keys(saveValue.grManagementData).length > 0) {
            setSelectedMilestone({
                ...selectedMilestone,
                ...saveValue.grManagementData,
                // Ensure attachments are normalized (empty array strings become empty strings)
                gr_attachments: normalizeAttachmentValue(saveValue.grManagementData.gr_attachments),
                kf_attachments: normalizeAttachmentValue(saveValue.grManagementData.kf_attachments),
            });
        }

        // Enter edit mode
        originalEditHandler();
    }, [getSaveParamsValue, grManagementPageKey, projectId, selectedMilestone, deleteSaveParamsByPageKey, setSelectedMilestone, normalizeAttachmentValue]);

    // Clear auto-save after successful save
    const clearGRManagementAutoSave = useCallback(() => {
        if (grManagementPageKey) {
            deleteSaveParamsByPageKey(grManagementPageKey);
        }
    }, [deleteSaveParamsByPageKey, grManagementPageKey]);

    // Check if there are unsaved changes for RoutePrompt
    // Compare current selectedMilestone with initialParamsRef
    const hasUnsavedChanges = useMemo(() => {
        if (!isEditing || !selectedMilestone) return false;
        
        const initialData = initialParamsRef.current?.grManagementData;
        
        // If no initial data, no changes
        if (!initialData || Object.keys(initialData).length === 0) return false;
        
        // Compare the editable fields with normalized attachment values
        const hasChanges = 
            initialData.gate_review_decision !== (selectedMilestone?.gate_review_decision || '') ||
            initialData.gr_attachments !== normalizeAttachmentValue(selectedMilestone?.gr_attachments) ||
            initialData.key_feedback !== (selectedMilestone?.key_feedback || '') ||
            initialData.kf_attachments !== normalizeAttachmentValue(selectedMilestone?.kf_attachments);
        
        return hasChanges;
    }, [isEditing, selectedMilestone, normalizeAttachmentValue]);

    return {
        // State
        autoSaveVisible,
        grManagementPageKey,
        hasUnsavedChanges,

        // Handlers
        handleGRManagementEdit,
        handleAutoSaveCancel,
        handleAutoSaveConfirm,
        clearGRManagementAutoSave,
    };
};
